import React, { useMemo, useState } from 'react'
import { useSmartTranslation } from '../hooks/useSmartTranslation'
import { usePartProgress } from './usePartProgress'
import { STEP_STATES, buildProgressTree, getProgressSummary, getActiveStepIndex, formatRunningInfo } from './partProgressUtils'
import { PartProgressHeader, StepTooltip, stateGlyph } from './PartProgressBits'
import { DeviceTypeIcon } from './deviceTypeIcon'
import { useDragScroll } from './useDragScroll'
import './PartProgress.css'

/**
 * V3 — "Collapsed rail" (Stitch concept 3), now multi-level.
 *
 * The main rail shows the MAIN workflow as rounded-rect chips; every
 * subworkflow collapses into a clickable "⧉" chip with a step-count badge and
 * a fold caret. Expanded subworkflows render as an indented lane hanging from
 * an elbow connector — recursively, since subworkflows can nest arbitrarily
 * deep (Workflow.jsx). The lane containing the active step is auto-expanded
 * (following the part as it moves); any block can be folded/unfolded by hand
 * and the user's choice wins over the automatic one. Sibling blocks behave as
 * an ACCORDION: at most one subworkflow expanded per level — expanding one
 * implicitly folds the previously open sibling. Every level scrolls
 * horizontally on its own when the route outgrows the strip. Steps show a
 * device-type icon (same domain enum as the monitoring hero photos) telling
 * WHERE they execute.
 *
 * @param {Object} props
 * @param {String|Number} props.partId Part id (polls when given)
 * @param {Array} props.partProgress Route pushed via props
 * @param {String} props.partCode Optional part code for the header block
 */
const PartProgressTwoLevelRail = ({ partId = '', partProgress = [], partCode = '' }) => {
  const { t_ } = useSmartTranslation()
  const steps = usePartProgress(partId, partProgress)
  // Accordion per level: at most ONE subworkflow expanded among siblings.
  // Keyed by the parent scope ('' = main rail, parent pathKey deeper down);
  // the value is the expanded child's pathKey (null = all folded). Unset =
  // automatic: the sibling containing the active step is the expanded one.
  const [accordionSelection, setAccordionSelection] = useState({})
  // Mouse drag-to-scroll on every strip (touch pans natively)
  const dragScroll = useDragScroll()

  const tree = useMemo(() => buildProgressTree(steps), [steps])
  const activeIndex = getActiveStepIndex(steps)

  if (steps.length === 0) {
    return <div className='ppx-empty'>{t_('No part being processed')}</div>
  }

  const summary = getProgressSummary(steps)

  const groupState = (group) => {
    if (group.flatItems.some((item) => item.state === STEP_STATES.FAILED)) return STEP_STATES.FAILED
    if (group.flatItems.some((item) => item.state === STEP_STATES.WARNING)) return STEP_STATES.WARNING
    if (group.flatItems.some((item) => item.state === STEP_STATES.ACTIVE)) return STEP_STATES.ACTIVE
    if (group.flatItems.every((item) => item.state === STEP_STATES.PASSED || item.state === STEP_STATES.SKIPPED)) return STEP_STATES.PASSED
    return STEP_STATES.PENDING
  }

  const groupContainsActive = (group) => group.flatItems.some((item) => item.index === activeIndex)
  const isExpanded = (group, parentKey) => {
    const selected = accordionSelection[parentKey]
    if (selected !== undefined) {
      return selected === group.pathKey
    }
    return groupContainsActive(group)
  }
  const toggleGroup = (group, parentKey) => {
    const expanded = isExpanded(group, parentKey)
    // Selecting a sibling implicitly folds the previously expanded one
    setAccordionSelection((previous) => ({ ...previous, [parentKey]: expanded ? null : group.pathKey }))
  }

  const chipConnector = (state, key) => (
    <span
      key={key}
      className={
        'ppx-twolevel__connector' +
        (state === STEP_STATES.PASSED || state === STEP_STATES.FAILED ? ' ppx-twolevel__connector--' + state : '')
      }
    />
  )

  const renderStepChip = ({ step, index, state }) => {
    const isActive = state === STEP_STATES.ACTIVE || state === STEP_STATES.WARNING
    const runningInfo = isActive ? formatRunningInfo(step) : ''
    return (
      <StepTooltip step={step} key={'step-' + index}>
        <span className={'ppx-twolevel__chip ppx-twolevel__chip--' + state}>
          <span className='ppx-twolevel__chip-glyph'>{stateGlyph(state) || (isActive ? '●' : '○')}</span>
          <DeviceTypeIcon deviceTypeName={step.deviceTypeName} />
          {/* Job name over technology (PO): "Mill-1" says more than "Mill" */}
          {step.name || step.technologyName}
          {runningInfo ? <span className='ppx-twolevel__chip-live'> · {runningInfo}</span> : ''}
        </span>
      </StepTooltip>
    )
  }

  const renderGroupChip = (group, parentKey) => {
    const state = groupState(group)
    const nestedActive = groupContainsActive(group)
    const expanded = isExpanded(group, parentKey)
    return (
      <button
        key={group.pathKey}
        type='button'
        onClick={() => toggleGroup(group, parentKey)}
        className={
          'ppx-twolevel__chip ppx-twolevel__chip--sub ppx-twolevel__chip--' + state +
          (nestedActive ? ' ppx-twolevel__chip--nested-active' : '')
        }
        title={group.name + ' · ' + t_('Subworkflow')}
        aria-expanded={expanded}
      >
        <span className='ppx-twolevel__badge'>{group.flatItems.length}</span>
        <span className='ppx-twolevel__chip-glyph'>⧉</span>
        {group.name}
        <span className='ppx-twolevel__caret'>{expanded ? '▾' : '▸'}</span>
      </button>
    )
  }

  /**
   * Renders one nesting level: a horizontal chip row (the main rail at depth 0,
   * an indented amber lane deeper down) followed — depth-first — by the lane of
   * its (at most one, accordion) expanded subworkflow chip. `parentKey` is the
   * accordion scope shared by the level's sibling groups.
   */
  const renderLevel = (entries, depth, label, parentKey) => {
    const rowChips = []
    entries.forEach((entry, entryIndex) => {
      const state = entry.type === 'step' ? entry.item.state : groupState(entry)
      rowChips.push(entry.type === 'step' ? renderStepChip(entry.item) : renderGroupChip(entry, parentKey))
      if (entryIndex < entries.length - 1) {
        rowChips.push(chipConnector(state, 'connector-' + depth + '-' + entryIndex))
      }
    })

    const expandedLanes = entries
      .filter((entry) => entry.type === 'group' && isExpanded(entry, parentKey))
      .map((group) => (
        <React.Fragment key={'lane-' + group.pathKey}>
          {renderLevel(group.children, depth + 1, group.name, group.pathKey)}
        </React.Fragment>
      ))

    if (depth === 0) {
      return (
        <>
          <div className='ppx-scroll' {...dragScroll}>
            <div className='ppx-twolevel__mainrail'>{rowChips}</div>
          </div>
          {expandedLanes}
        </>
      )
    }
    return (
      <>
        <div className='ppx-twolevel__lane-row' style={{ marginLeft: 34 + (depth - 1) * 26 }}>
          <div className='ppx-twolevel__lane' {...dragScroll}>
            <span className='ppx-twolevel__lane-label'>⧉ {label}</span>
            {rowChips}
          </div>
        </div>
        {expandedLanes}
      </>
    )
  }

  return (
    <div className='ppx-strip'>
      <PartProgressHeader steps={steps} partCode={partCode} summary={summary} />
      <div className='ppx-twolevel'>
        {renderLevel(tree, 0, '', '')}
      </div>
    </div>
  )
}

export default PartProgressTwoLevelRail
